// src/components/students/InvitationDialog.tsx

import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    TextField,
    InputAdornment,
    IconButton,
    Alert,
    Chip,
} from '@mui/material';
import {
    ContentCopy as CopyIcon,
    Check as CheckIcon,
    Email as EmailIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import ru from 'date-fns/locale/ru';

interface InvitationDialogProps {
    open: boolean;
    onClose: () => void;
    token: string;
    expiresAt: string;
    studentName: string;
}

const InvitationDialog: React.FC<InvitationDialogProps> = ({
                                                               open,
                                                               onClose,
                                                               token,
                                                               expiresAt,
                                                               studentName,
                                                           }) => {
    const [copied, setCopied] = useState(false);

    // Формируем полную ссылку на фронте
    const invitationLink = `${window.location.origin}/invite/${token}`;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(invitationLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error('Не удалось скопировать:', error);
        }
    };

    // Форматируем дату
    const formatExpiresAt = (): string => {
        try {
            return format(parseISO(expiresAt), 'd MMMM yyyy, HH:mm', { locale: ru });
        } catch {
            return expiresAt;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: { sx: { borderRadius: 2 } },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="primary" />
                    <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                        Ссылка-приглашение
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                <Alert severity="success" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        Ученик <strong>{studentName}</strong> создан.
                        Отправьте ему ссылку для регистрации:
                    </Typography>
                </Alert>

                <TextField
                    fullWidth
                    value={invitationLink}
                    slotProps={{
                        input: {
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleCopy}
                                        edge="end"
                                        color={copied ? 'success' : 'primary'}
                                    >
                                        {copied ? <CheckIcon /> : <CopyIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{
                        mb: 2,
                        '& .MuiInputBase-input': {
                            fontSize: '0.85rem',
                        },
                    }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                        label={`Действительна до ${formatExpiresAt()}`}
                        size="small"
                        color="warning"
                        variant="outlined"
                    />
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="caption">
                        Ссылка одноразовая. После регистрации студента она станет недействительной.
                        Если срок действия истёк — сгенерируйте новую в списке учеников.
                    </Typography>
                </Alert>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
                <Button onClick={handleCopy} startIcon={copied ? <CheckIcon /> : <CopyIcon />}>
                    {copied ? 'Скопировано' : 'Скопировать ссылку'}
                </Button>
                <Button onClick={onClose} variant="contained">
                    Понятно
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InvitationDialog;